import { SingleIndex } from '@deqo/foundation';
import { DoubleMap } from '../../CodesForFoundation';
import { Convention } from './Convention';
import {
    ComponentDefinition,
    ComponentDescriptor,
    ComponentType, FieldDescriptor,
    FieldType,
    PropertyDescriptor
} from './Interfaces';

const throwOrRestart = (error: string) => {
    // if (Host.isDev) (window as any).location.reload();
    throw new Error(error);
};

const componentRegister = new Map<ComponentType | string, ComponentDescriptor>();
const fieldRegister = new DoubleMap<ComponentType, string, FieldDescriptor>();
const propertyRegister = new DoubleMap<ComponentType | FieldType, string, PropertyDescriptor>();
const conventionToType = new SingleIndex<Convention<any>, ComponentType>();
const typeToConvention = new SingleIndex<ComponentType, Convention<any>>();

const getPrototypeChain = <T extends ComponentType | FieldType>(type: T, inclusive: boolean) => {
    const baseProto = Object.getPrototypeOf(Object);
    const chain = [type] as T[];
    while (true) {
        const next = Object.getPrototypeOf(chain[chain.length - 1]);
        if (next === baseProto || !next) break;
        chain.push(next);
    }
    return chain.slice(inclusive ? 0 : 1);
};

const registerInheritedPropertiesOnComponent = (type: ComponentType): void => {
    // For the specified component type, collect all the properties that exist on it's prototype chain and register properties directly on
    // the component type.  This means when we lookup a field from a base type, we don't have to re-walk the chain and will have a complete
    // index of properties for each type.
    const chain = getPrototypeChain(type, false);
    for (const link of chain) {
        for (const pd of Array.from(propertyRegister.values(link) || [])) {
            // Register the property on the input type
            registerProperty(type, pd);
        }
    }
};

const registerFieldPropertiesOnComponent = (type: ComponentType) => {
    // For the specified component type, collect all the fields that exist on it's prototype chain and register properties directly on the
    // component that are scoped (prefixed) with the field name.  So, if a field named "text" that has as property of "content" exists it
    // will generate a direct property on the component type of "text.content".
    const fields = getPrototypeChain(type, true).flatMap(ct => Array.from(fieldRegister.values(ct) || []));
    for (const fd of fields) {
        const properties = getPrototypeChain(fd.type, true).flatMap(ft => Array.from(propertyRegister.values(ft) || []));
        // For each property, create a copy, rename and register with the component type
        for (const pd of properties) {
            const name = `${fd.name}|${pd.name}`;
            const copy = Object.assign({}, pd, { name });
            registerProperty(type, copy);
        }
    }
};

const describeComponent = (typeOrTypeName: ComponentType | string): ComponentDescriptor => {
    return componentRegister.get(typeOrTypeName) || null;
};

const describeField = (hostType: ComponentType, propertyName: string): FieldDescriptor => {
    return fieldRegister.get(hostType, propertyName) || null;
};

const describeProperty = (hostType: ComponentType | FieldType, propertyName: string): PropertyDescriptor => {
    return propertyRegister.get(hostType, propertyName) || null;
};

const doesTypeImplementConvention = (type: ComponentType, conv: Convention): boolean => {
    return typeToConvention.get(type).has(conv);
};

const getTypesByConvention = <T>(conv: Convention<T>): ComponentType[] => {
    return Array.from(conventionToType.get(conv));
};

const registerComponent = (type: ComponentType, def: ComponentDefinition): void => {
    // Validate
    if (componentRegister.has(type)) throwOrRestart(`register failure: ${def.name} already registered.`);
    if (componentRegister.has(def.name)) throwOrRestart(`register failure: ${def.name} already registered.`);
    // First, register any properties from the base types of the type on the type directly.
    registerInheritedPropertiesOnComponent(type);
    // Then, register field properties on the type directly, but scoping to the field name.
    registerFieldPropertiesOnComponent(type);
    // Build the descriptor by getting the fields and properties registered to the type, setting the other required stuff and freezing.
    const desc = Object.freeze({
        ...def,
        type,
        implements: def.implements ?? [],
        fields: Array.from(fieldRegister.values(type) || []),
        properties: Array.from(propertyRegister.values(type) || []),
    });
    // Register
    componentRegister.set(desc.name, desc);
    componentRegister.set(desc.type, desc);
    // Map conventions
    for (const conv of desc.implements) {
        conventionToType.add(conv, desc.type);
        typeToConvention.add(desc.type, conv);
    }
};

const registerField = (hostType: ComponentType, desc: FieldDescriptor): void => {
    // Validate
    if (fieldRegister.has(hostType, desc.name)) throwOrRestart(`register failure: ${hostType.name}.${desc.name} already registered.`);
    fieldRegister.set(hostType, desc.name, Object.freeze({ ...desc }));
};

const registerProperty = (hostType: ComponentType | FieldType, desc: PropertyDescriptor): void => {
    // Don't re-register, we expect overrides first
    if (!propertyRegister.has(hostType, desc.name)) {
        propertyRegister.set(hostType, desc.name, Object.freeze(desc));
    }
    // If property has an alias, register it as well
    if (!!desc.alias && !propertyRegister.has(hostType, desc.alias)) {
        propertyRegister.set(hostType, desc.alias, Object.freeze(desc));
    }
};

const debugString = (toc: any) => {
    if (typeof toc === 'string') return toc;
    if (toc instanceof Convention) return toc.toString();
    const desc = describeComponent(toc);
    if (desc) return desc.name;
    return toc.toString();
};

export const Manifest = Object.freeze({
    describeComponent,
    describeField,
    describeProperty,
    doesTypeImplementConvention,
    getTypesByConvention,
    registerComponent,
    registerField,
    registerProperty,
    debugString,
});


